// The visitor's answer to "may we use analytics cookies?".
//
// Keeping the answer is itself strictly necessary (§ 25 (2) TDDDG) — without it the
// banner would reappear on every page — so it is stored whatever the answer is. It
// expires after a year, after which we ask again.

const KEY = 'chefty.analytics-consent';
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const SETTINGS_EVENT = 'chefty:consent-settings';

export type ConsentStatus = 'granted' | 'denied';

/** The stored answer, or null when the visitor hasn't answered (or it expired). */
export function readConsent(now = Date.now()): ConsentStatus | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { status, at } = JSON.parse(raw) as { status?: unknown; at?: unknown };
    if (status !== 'granted' && status !== 'denied') return null;
    if (typeof at !== 'number' || now - at > MAX_AGE_MS) return null;
    return status;
  } catch {
    // Storage blocked (some private modes): treat as unanswered, ask again.
    return null;
  }
}

export function writeConsent(status: ConsentStatus): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ status, at: Date.now() }));
  } catch {
    // Not persisted; the answer still applies to this page view.
  }
}

/** Reopen the banner — withdrawing consent must be as easy as giving it (Art. 7 (3) GDPR). */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event(SETTINGS_EVENT));
}

export function onConsentSettingsRequested(listener: () => void): () => void {
  window.addEventListener(SETTINGS_EVENT, listener);
  return () => window.removeEventListener(SETTINGS_EVENT, listener);
}
